// @ts-strict-ignore
import * as React from 'react';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';

import {confirm} from 'common/components/confirm';
import {THEME as ConfirmTheme} from 'common/components/confirm/types';
import {InputReact} from 'common/components/input';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {InputSize} from 'common/components/input/types';
import {useThemes} from 'common/contexts/react/themes-context';
import {useTranslation} from 'common/i18n/hooks';
import type {Theme} from 'common/report/queries/app-api/themes';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';

import styles from './index.module.styl';
import {STATIC_THEMES} from './constants';
import {ColorThemeEditor} from './editor';
import {ColorThemeSwatch, SwatchOverflowMenuClickCallback} from './swatch';
import {getThemeTrackingProps, resolveTheme} from './utils';
import {launchSaveThemeModal, SaveThemeModalResult} from './save-theme-modal';

type ThemePickerParams = {
  allowCreate?: boolean;
  /** defines which theme will be highlighted as selected */
  activeThemeId?: number | string;
  /** allows the theme list to render a custom theme (a non persisted, or static theme) */
  customTheme?: Theme;
  onThemeSelect?: (theme: Theme) => void;
  disableSelectTracking?: boolean;
};

export function ChartThemePicker(props: ThemePickerParams) {
  const {t} = useTranslation();
  const themesResource = useThemes();
  const defaultProjectThemeId = themesResource.getDefaultProjectThemeId();
  const [creatingNewTheme, setCreatingNewTheme] = React.useState(false);
  const [editingTheme, setEditingTheme] = React.useState<Theme>(null);
  const [filter, setFilter] = React.useState(``);

  function trackSave({theme, oldTheme}: {theme: Theme; oldTheme?: Theme}) {
    let themeUpdateTrackingProps = {};
    if (oldTheme) {
      themeUpdateTrackingProps = {
        nameChanged: oldTheme.name !== theme.name,
        colorsChanged:
          !isEqual(oldTheme.data.colors, theme.data.colors) ||
          !isEqual(oldTheme.data.darkColors, theme.data.darkColors),
        sharingChanged: oldTheme.global_access_type !== theme.global_access_type,
      };
    }
    mixpanel.track(`[Theme] Save`, {
      isNewTheme: !oldTheme,
      ...getThemeTrackingProps(theme),
      ...themeUpdateTrackingProps,
    });
  }

  /**
   * intentionally populate `props.customTheme` into state so that if a
   * bookmark with a custom theme is loaded, it does not dissapear from
   * the list of themes, when changing the selected theme
   */
  const [customTheme, setCustomTheme] = React.useState(props.customTheme);

  /** sets the selected theme to either the project default, a custom bookmark theme, or the provided theme id */
  const selectedTheme =
    (props.activeThemeId && {id: props.activeThemeId}) ||
    props.customTheme ||
    (defaultProjectThemeId && {id: defaultProjectThemeId});

  const activeTheme = resolveTheme({theme: selectedTheme, themesResource});
  const themes: Theme[] = [
    // loads themes defined in bookmark params
    ...(customTheme ? [customTheme] : []),
    ...STATIC_THEMES,
    ...themesResource.getAllValues(),
  ];

  const handleApplyTheme = (theme: Theme, isCustom = false) => {
    setCreatingNewTheme(false);
    setEditingTheme(null);

    if (props.onThemeSelect) {
      /** when selecting the default theme, remove all theme data from bookmarks */
      props.onThemeSelect(theme.id === defaultProjectThemeId ? null : theme);
    }

    if (isCustom) {
      setCustomTheme(theme);
    }
  };

  const onSave = ({theme, oldTheme}: {theme: Theme; oldTheme?: Theme}) => {
    handleApplyTheme(theme);
    trackSave({theme, oldTheme});
  };

  const handleThemeSwatchClick = (theme: Theme) => {
    if (activeTheme.id === theme.id) {
      return;
    }

    handleApplyTheme(theme);

    !props.disableSelectTracking && mixpanel.track(`[Theme] Select Theme`, getThemeTrackingProps(theme));
  };

  const onOverflowMenuClick: SwatchOverflowMenuClickCallback = async ({action, theme}) => {
    switch (action) {
      case `edit`:
        setEditingTheme(theme);
        break;
      case `share`:
        launchSaveThemeModal({theme})
          .then(({updatedTheme}: SaveThemeModalResult) => {
            onSave({theme: updatedTheme, oldTheme: theme});
          })
          .catch(() => {
            // Modal was closed
          });
        break;
      case `delete`:
        if (!theme.id) {
          // this is an inline theme. just remove it from the bookmark
          props.onThemeSelect(null);
          setCustomTheme(null);
        } else if (
          await confirm({
            title: t(`chart-customization.delete-theme`),
            description: t(`chart-customization.confirm-delete-theme`, {themeName: theme.name}),
            theme: ConfirmTheme.DELETE,
            'confirm-button-label': t(`common.delete`),
          })
        ) {
          themesResource
            .delete(theme.id as number)
            .then(() => {
              addToast({message: t(`chart-customization.theme-deleted`), type: ToastType.DESTRUCTIVE});
              if (theme.id === activeTheme.id) {
                // clear the selected theme if we just deleted it
                props.onThemeSelect(null);
              }
            })
            .catch(() => {
              addToast({message: t(`chart-customization.error-deleting-theme`), type: ToastType.ERROR});
            });
        }
        break;
      default:
        break;
    }
  };

  const filteredThemes = filter
    ? themes.filter((theme) => theme.name.toLowerCase().includes(filter.toLowerCase()))
    : themes;

  return (
    <div className={clsx(styles.chartColors)}>
      {creatingNewTheme ? (
        <ColorThemeEditor
          theme={{...activeTheme, id: undefined}}
          onSave={(theme) => onSave({theme})}
          onApply={(theme) => {
            handleApplyTheme(theme, true);
            mixpanel.track(`[Theme] Apply New Theme`, getThemeTrackingProps(theme));
          }}
          onCancel={() => {
            setCreatingNewTheme(false);
          }}
        />
      ) : editingTheme ? (
        <ColorThemeEditor
          theme={editingTheme}
          onSave={(theme) => onSave({theme, oldTheme: editingTheme})}
          onApply={(theme) => handleApplyTheme(theme, true)}
          onCancel={() => {
            setEditingTheme(null);
          }}
        />
      ) : (
        <>
          <div className={clsx(styles.searchLine)}>
            <InputReact
              placeholder={t(`chart-customization.search-themes-placeholder`)}
              icon='search'
              size={props.allowCreate ? InputSize.Medium : InputSize.Large}
              onInput={(ev) => setFilter(ev.target.value)}
            />
            {props.allowCreate && (
              <ButtonReact
                className={clsx(styles.button)}
                theme={ButtonTheme.Secondary}
                icon='plus'
                onClick={() => {
                  setCreatingNewTheme(true);
                }}
              >
                {t(`common.new`)}
              </ButtonReact>
            )}
          </div>
          <div className={clsx(styles.list, {[styles.empty]: filteredThemes.length === 0})}>
            {filteredThemes.length
              ? filteredThemes.map((theme) => (
                  <ColorThemeSwatch
                    key={`${theme.id}`}
                    theme={theme}
                    onSwatchClick={() => handleThemeSwatchClick(theme)}
                    isSelected={activeTheme.id === theme.id}
                    tag={defaultProjectThemeId === theme.id ? t(`common.default`) : ``}
                    darkMode={themesResource.isDarkMode()}
                    overflowMenu={
                      props.allowCreate &&
                      ((Number.isInteger(theme.id) && theme.can_update_basic) || !theme.id) && (
                        // displays the overflow menu for themes the user has access to and bookmark defined themes
                        <SelectReact
                          theme={SelectTheme.Tertiary}
                          icon={{leftIcon: `hor-ellipsis`}}
                          iconOnly
                          menuWidth={120}
                          sections={[
                            {
                              items: [
                                {label: t(`common.edit`), value: `edit`, icon: `edit`},
                                {label: t(`common.share`), value: `share`, icon: `invite`},
                                {label: t(`common.delete`), value: `delete`, icon: `trashcan`},
                              ],
                            },
                          ]}
                          onChange={(ev) => onOverflowMenuClick({action: ev.detail.item.value, theme})}
                        />
                      )
                    }
                  />
                ))
              : filter
                ? t(`query-builder.no-search-results`)
                : t(`chart-customization.no-themes-available`)}
          </div>
        </>
      )}
    </div>
  );
}
